import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { supabase } from './supabaseClient';
import { Exam, Topic } from './examData';

interface AnswerRow {
    exam_id: string;
    question_key: string;
    selected_index: number;
    correct_index: number;
    is_correct: boolean;
}

interface ExamProgress {
    exam: Exam;
    answered: number;
    correct: number;
    total: number;
    percentage: number;
    status: 'completed' | 'in-progress';
}

interface TopicStat {
    id: string;
    name: string;
    description: string;
    correct: number;
    total: number;
    percentage: number;
}

const PageContainer = styled.div`
    min-height: calc(100vh - 56px);
    background-color: #f8fafc;
    padding: 40px 20px;
`;

const Content = styled.div`
    max-width: 960px;
    margin: 0 auto;
`;

const PageTitle = styled.h1`
    color: #00205B;
    font-size: 2rem;
    font-weight: 700;
    margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
    color: #64748b;
    font-size: 1rem;
    margin: 0 0 32px 0;
`;

const Section = styled.div`
    margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
    color: #1e293b;
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Badge = styled.span`
    font-size: 0.75rem;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 2px 8px;
    border-radius: 10px;
`;

const EmptyState = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 32px;
    text-align: center;
    color: #94a3b8;
    font-size: 0.95rem;
`;

const ExamCard = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #00205B;
        box-shadow: 0 2px 8px rgba(0, 32, 91, 0.1);
    }
`;

const ExamInfo = styled.div`
    flex: 1;
`;

const ExamName = styled.div`
    font-weight: 700;
    color: #1e293b;
    font-size: 1rem;
    margin-bottom: 4px;
`;

const ExamMeta = styled.div`
    color: #64748b;
    font-size: 0.85rem;
`;

const ScoreChip = styled.div<{ color: string }>`
    text-align: center;
    min-width: 80px;
`;

const ScoreValue = styled.div<{ color: string }>`
    font-size: 1.5rem;
    font-weight: 700;
    color: ${props => props.color};
`;

const ScoreLabel = styled.div`
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 600;
`;

const ProgressBarOuter = styled.div`
    width: 120px;
    height: 8px;
    background: #e2e8f0;
    border-radius: 4px;
    margin-top: 8px;
`;

const ProgressBarInner = styled.div<{ width: number; color: string }>`
    height: 100%;
    width: ${props => props.width}%;
    background: ${props => props.color};
    border-radius: 4px;
    transition: width 0.3s ease;
`;

const TopicGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 12px;
`;

const TopicCard = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
`;

const TopicName = styled.div`
    font-weight: 700;
    color: #1e293b;
    font-size: 0.95rem;
    margin-bottom: 4px;
`;

const TopicDesc = styled.div`
    color: #64748b;
    font-size: 0.8rem;
    margin-bottom: 12px;
`;

const TopicScore = styled.div<{ color: string }>`
    font-size: 0.9rem;
    font-weight: 700;
    color: ${props => props.color};
`;

const TopicBarOuter = styled.div`
    width: 100%;
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    margin-top: 8px;
`;


const SuggestBanner = styled.div`
    background: linear-gradient(135deg, #00205B, #004b8d);
    border-radius: 12px;
    padding: 24px;
    color: #ffffff;
    margin-bottom: 12px;
`;

const SuggestTitle = styled.div`
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 8px;
`;

const SuggestText = styled.div`
    font-size: 0.9rem;
    opacity: 0.9;
    line-height: 1.5;
`;

const CourseGroupHeader = styled.div`
    font-size: 0.95rem;
    font-weight: 700;
    color: #00205B;
    padding: 8px 0 4px 0;
    margin-top: 8px;
    border-bottom: 2px solid #00205B;
    margin-bottom: 12px;

    &:first-child {
        margin-top: 0;
    }
`;

const RetakeButton = styled.button`
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 700;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    background: transparent;
    color: #00205B;
    border: 2px solid #00205B;

    &:hover {
        background: #00205B;
        color: #ffffff;
    }
`;

interface DashboardProps {
    userId: string;
    exams: Exam[];
    topics: Topic[];
    onSelectExam: (exam: Exam) => void;
    onRetakeExam: (exam: Exam) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ userId, exams, topics, onSelectExam, onRetakeExam }) => {
    const [answers, setAnswers] = useState<AnswerRow[]>([]);
    const [loading, setLoading] = useState(true);

    const loadAnswers = useCallback(async () => {
        setLoading(true);
        const { data } = await supabase
            .from('user_answers')
            .select('exam_id, question_key, selected_index, correct_index, is_correct')
            .eq('user_id', userId);
        setAnswers(data || []);
        setLoading(false);
    }, [userId]);

    useEffect(() => {
        loadAnswers();
    }, [loadAnswers]);

    const dedupedAnswers = React.useMemo(() => {
        const latest: Record<string, AnswerRow> = {};
        answers.forEach(a => {
            const key = `${a.exam_id}::${a.question_key}`;
            latest[key] = a;
        });
        return Object.values(latest);
    }, [answers]);

    const customExamMeta: Record<string, { id: string; courseId: string; title: string; questionCount: number }> = React.useMemo(() => {
        return JSON.parse(localStorage.getItem('customExams') || '{}');
    }, []);

    const examProgressList: ExamProgress[] = React.useMemo(() => {
        const byExam: Record<string, AnswerRow[]> = {};
        dedupedAnswers.forEach(a => {
            if (!byExam[a.exam_id]) byExam[a.exam_id] = [];
            byExam[a.exam_id].push(a);
        });

        return Object.entries(byExam)
            .map(([examId, rows]) => {
                const exam = exams.find(e => e.id === examId);
                const custom = customExamMeta[examId];
                if (!exam && !custom) return null;
                const answered = rows.length;
                const correct = rows.filter(r => r.is_correct).length;
                const total = exam
                    ? exam.questions.filter(q => !q.freeResponse).length
                    : custom.questionCount;
                const status = answered >= total ? 'completed' : 'in-progress';
                const examObj: Exam = exam || {
                    id: custom.id,
                    courseId: custom.courseId,
                    title: custom.title,
                    semester: '',
                    year: 0,
                    questions: [],
                };
                return {
                    exam: examObj,
                    answered,
                    correct,
                    total,
                    percentage: total > 0 ? Math.round((correct / total) * 100) : 0,
                    status,
                } as ExamProgress;
            })
            .filter(Boolean) as ExamProgress[];
    }, [dedupedAnswers, exams, customExamMeta]);

    const topicStats: TopicStat[] = React.useMemo(() => {
        const stats: Record<string, { correct: number; total: number }> = {};
        topics.forEach(t => { stats[t.id] = { correct: 0, total: 0 }; });

        dedupedAnswers.forEach(a => {
            const exam = exams.find(e => e.id === a.exam_id);
            if (!exam) return;
            const question = exam.questions.find((q, idx) => {
                const k = `${idx}-${q.id}${q.sub || ''}`;
                return k === a.question_key;
            });
            if (!question) return;
            question.topics.forEach(topicId => {
                if (stats[topicId]) {
                    stats[topicId].total++;
                    if (a.is_correct) stats[topicId].correct++;
                }
            });
        });

        return topics
            .map(t => ({
                ...t,
                correct: stats[t.id].correct,
                total: stats[t.id].total,
                percentage: stats[t.id].total > 0
                    ? Math.round((stats[t.id].correct / stats[t.id].total) * 100)
                    : -1,
            }))
            .filter(t => t.total > 0)
            .sort((a, b) => a.percentage - b.percentage);
    }, [dedupedAnswers, exams, topics]);

    const regularExams = examProgressList.filter(ep => !ep.exam.id.startsWith('custom-'));
    const customExams = examProgressList.filter(ep => ep.exam.id.startsWith('custom-'));
    const completed = regularExams.filter(e => e.status === 'completed');
    const inProgress = regularExams.filter(e => e.status === 'in-progress');
    const weakTopics = topicStats.filter(t => t.percentage < 70);

    const courseIds = Array.from(new Set(regularExams.map(ep => ep.exam.courseId))).sort();
    const topicsByCourse: Record<string, TopicStat[]> = {};
    courseIds.forEach(cid => {
        const prefix = cid === 'MATH 101' ? 'CI-' : cid === 'MATH 102' ? 'CII-' : '';
        const courseTopics = topicStats.filter(t => prefix && t.id.startsWith(prefix));
        if (courseTopics.length > 0) topicsByCourse[cid] = courseTopics;
    });
    const hasMultipleCourses = courseIds.length > 1;

    const getScoreColor = (pct: number) => {
        if (pct >= 80) return '#22c55e';
        if (pct >= 60) return '#f59e0b';
        return '#ef4444';
    };

    if (loading) {
        return (
            <PageContainer>
                <Content>
                    <PageTitle>My Dashboard</PageTitle>
                    <EmptyState>Loading your progress...</EmptyState>
                </Content>
            </PageContainer>
        );
    }

    if (answers.length === 0) {
        return (
            <PageContainer>
                <Content>
                    <PageTitle>My Dashboard</PageTitle>
                    <PageSubtitle>Track your exam progress and see what to study next</PageSubtitle>
                    <EmptyState>No exam attempts yet. Go take a practice exam to see your stats here!</EmptyState>
                </Content>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Content>
                <PageTitle>My Dashboard</PageTitle>
                <PageSubtitle>Track your exam progress and see what to study next</PageSubtitle>

                {weakTopics.length > 0 && (
                    <Section>
                        <SuggestBanner>
                            <SuggestTitle>Focus Areas</SuggestTitle>
                            <SuggestText>
                                Based on your answers, you should focus on:{' '}
                                {weakTopics.map((t, i) => (
                                    <span key={t.id}>
                                        <strong>{t.name}</strong> ({t.percentage}%)
                                        {i < weakTopics.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                            </SuggestText>
                        </SuggestBanner>
                    </Section>
                )}

                {courseIds.map(courseId => {
                    const courseInProgress = inProgress.filter(ep => ep.exam.courseId === courseId);
                    const courseCompleted = completed.filter(ep => ep.exam.courseId === courseId);
                    const prefix = courseId === 'MATH 101' ? 'CI-' : courseId === 'MATH 102' ? 'CII-' : '';
                    const courseTopics = topicStats.filter(t => prefix && t.id.startsWith(prefix));

                    return (
                        <Section key={courseId}>
                            {hasMultipleCourses && (
                                <CourseGroupHeader>{courseId}</CourseGroupHeader>
                            )}

                            {courseInProgress.length > 0 && (
                                <div>
                                    <SectionTitle>
                                        In Progress <Badge>{courseInProgress.length}</Badge>
                                    </SectionTitle>
                                    {courseInProgress.map(ep => (
                                        <ExamCard key={ep.exam.id} onClick={() => onSelectExam(ep.exam)}>
                                            <ExamInfo>
                                                <ExamName>{ep.exam.title}</ExamName>
                                                <ExamMeta>
                                                    {ep.exam.courseId} &middot; {ep.answered}/{ep.total} answered
                                                </ExamMeta>
                                                <ProgressBarOuter>
                                                    <ProgressBarInner
                                                        width={(ep.answered / ep.total) * 100}
                                                        color="#00205B"
                                                    />
                                                </ProgressBarOuter>
                                            </ExamInfo>
                                            <ScoreChip color={getScoreColor(ep.percentage)}>
                                                <ScoreValue color={getScoreColor(ep.percentage)}>
                                                    {ep.percentage}%
                                                </ScoreValue>
                                                <ScoreLabel>{ep.correct}/{ep.answered} correct</ScoreLabel>
                                            </ScoreChip>
                                        </ExamCard>
                                    ))}
                                </div>
                            )}

                            {courseCompleted.length > 0 && (
                                <div>
                                    <SectionTitle>
                                        Completed <Badge>{courseCompleted.length}</Badge>
                                    </SectionTitle>
                                    {courseCompleted.map(ep => (
                                        <ExamCard key={ep.exam.id} onClick={() => onSelectExam(ep.exam)}>
                                            <ExamInfo>
                                                <ExamName>{ep.exam.title}</ExamName>
                                                <ExamMeta>
                                                    {ep.exam.courseId} &middot; {ep.total} questions
                                                </ExamMeta>
                                            </ExamInfo>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <ScoreChip color={getScoreColor(ep.percentage)}>
                                                    <ScoreValue color={getScoreColor(ep.percentage)}>
                                                        {ep.percentage}%
                                                    </ScoreValue>
                                                    <ScoreLabel>{ep.correct}/{ep.total} correct</ScoreLabel>
                                                </ScoreChip>
                                                <RetakeButton onClick={(e) => {
                                                    e.stopPropagation();
                                                    onRetakeExam(ep.exam);
                                                }}>
                                                    Retake
                                                </RetakeButton>
                                            </div>
                                        </ExamCard>
                                    ))}
                                </div>
                            )}

                            {courseTopics.length > 0 && (
                                <div>
                                    <SectionTitle>Topic Breakdown</SectionTitle>
                                    <TopicGrid>
                                        {courseTopics.map(t => (
                                            <TopicCard key={t.id}>
                                                <TopicName>{t.name}</TopicName>
                                                <TopicDesc>{t.description}</TopicDesc>
                                                <TopicScore color={getScoreColor(t.percentage)}>
                                                    {t.correct}/{t.total} correct ({t.percentage}%)
                                                </TopicScore>
                                                <TopicBarOuter>
                                                    <ProgressBarInner
                                                        width={t.percentage}
                                                        color={getScoreColor(t.percentage)}
                                                    />
                                                </TopicBarOuter>
                                            </TopicCard>
                                        ))}
                                    </TopicGrid>
                                </div>
                            )}
                        </Section>
                    );
                })}

                {customExams.length > 0 && (
                    <Section>
                        <CourseGroupHeader>Custom Exams</CourseGroupHeader>
                        {customExams.map(ep => (
                            <ExamCard key={ep.exam.id}>
                                <ExamInfo>
                                    <ExamName>{ep.exam.title}</ExamName>
                                    <ExamMeta>
                                        {ep.exam.courseId} &middot; {ep.answered}/{ep.total} answered
                                    </ExamMeta>
                                </ExamInfo>
                                <ScoreChip color={getScoreColor(ep.percentage)}>
                                    <ScoreValue color={getScoreColor(ep.percentage)}>
                                        {ep.percentage}%
                                    </ScoreValue>
                                    <ScoreLabel>{ep.correct}/{ep.total} correct</ScoreLabel>
                                </ScoreChip>
                            </ExamCard>
                        ))}
                    </Section>
                )}
            </Content>
        </PageContainer>
    );
};

export default Dashboard;
